import React, {useState} from "react";
import clsx from "clsx";
import {Dropdown, Menu, Modal, Spin, Tooltip} from "antd";
import {useDispatch, useSelector} from "react-redux";
import {Link, useHistory, useLocation} from "react-router-dom";
import {DownOutlined, SettingFilled, ToolFilled} from "@ant-design/icons";

import {
    EDIT_SENSORS_IMAGES,
    EDIT_SENSORS_NAMES,
    EDIT_SENSORS_TYPES,
    EDIT_WARNINGS,
    CONFIGURE_SILO_NO_ID
} from "../PrivateRoute/components/constants";
import {ReactComponent as Key} from "../../assets/icons/key_setting.svg";
import {CustomButton} from "../Button";
import {useCurrentSelection} from "../../hooks/useCurrentSelection";
import {selectSensorsState} from "../../store/selectors";
import {sensorsAC} from "../../store/branches/sensors/actionCreators";

import classes from "./SettingPopup.module.scss";
import {LoadingStatus} from "../../store/status";
import {usePermissions} from "../../hooks/usePermissions";

const SettingPopup: React.FC = () => {
    const [modal, setModal] = useState(false);
    const history = useHistory();
    const dispatch = useDispatch();
    const {pathname} = useLocation();

    const {warnings, status_warning} = useSelector(selectSensorsState);
    const {device} = useCurrentSelection();
    const {isSuperUser} = usePermissions();

    const isWarningLoading = status_warning === LoadingStatus.LOADING;
    const isDashboard = pathname === "/dashboard";
    const openWarningsModal = () => {
        if (!device) {
            dispatch(sensorsAC.setSensorsStatusOperation(LoadingStatus.FETCH_SENSORS_WITHOUT_DEVICE));
            return;
        }
        dispatch(sensorsAC.fetchWarnings(device.id.toString()));
        setModal(true);
    };

    const onCancel = () => {
        setModal(false);
        dispatch(sensorsAC.setWarningsSign(null));
    };
    const fixNotification = () => {
        dispatch(sensorsAC.fixNotification({device_id: device.id}));
    };

    const onAddWarning = () => {
        history.push(EDIT_WARNINGS);
    };

    const title = (
        <div>
            <Key/><span className={classes.modalTitle}>Warnings</span>
        </div>
    );

    const menu = (
        <Menu>
            <Menu.Item key="0" icon={<ToolFilled/>} onClick={openWarningsModal}>
                <span>Warnings</span>
            </Menu.Item>
            <Menu.Divider className={classes.driver}/>

            <Menu.Item key="1" icon={<ToolFilled/>}>
                <Link to={EDIT_SENSORS_NAMES}>Sensor Names</Link>
            </Menu.Item>
            <Menu.Divider className={classes.driver}/>

            <Menu.Item key="2" icon={<ToolFilled/>}>
                <Link to={EDIT_SENSORS_IMAGES}>Sensor Images</Link>
            </Menu.Item>
            {
                isSuperUser && isDashboard
                &&
                <>
                    <Menu.Divider className={classes.driver}/>
                    <Menu.Item key="3" icon={<ToolFilled/>}>
                        <span onClick={fixNotification}>Fix Notification</span>
                    </Menu.Item>
                </>
            }
            {
                ["0005", "0006"].includes(device?.device_type)
                &&
                <React.Fragment>
                    <Menu.Divider className={classes.driver}/>
                    <Menu.Item key="4" icon={<ToolFilled/>}>
                        <Link to={EDIT_SENSORS_TYPES}>Sensor Settings</Link>
                    </Menu.Item>
                </React.Fragment>
            }
                 {
                ["0007","001W"].includes(device?.device_type) && device?.device_sub_type?.name !=="Roll Stand"
                &&
                <React.Fragment>
                    <Menu.Divider className={classes.driver}/>
                    <Menu.Item key="4" icon={<ToolFilled/>}>
                        <Link to={`${CONFIGURE_SILO_NO_ID}${device.id}`}>Silo Configuration</Link>
                    </Menu.Item>
                </React.Fragment>
            }
        </Menu>
    );

    const warningsList = (warnings && warnings?.map((item: any, index: number) => (
            <div key={`warning_sensor_${item.id}${index}}`} className={classes.mapShortNames}>
                <div className={classes.titleWrap}>
                    <Tooltip placement="topLeft" title={item?.name}>
                        <span className={classes.title}>
                            {item?.name}
                        </span>
                    </Tooltip>
                </div>
                <div className={classes.mapSign}>
                    <div>
                        {item?.sign}
                    </div>
                    <div>
                        {item?.value}
                    </div>
                    <div>
                        {item?.unit}
                    </div>
                </div>
            </div>
        ))
    );

    return (
        <React.Fragment>
            <div>
                <SettingFilled className="mar-right-10"/>

                <Dropdown overlay={menu}
                          trigger={["click"]}
                          placement="bottomLeft"
                          overlayClassName={classes.dropdown}>
                    <span className={clsx("ant-dropdown-link", classes.menuTitle)}
                          onClick={e => e.preventDefault()}
                    >
                        Setting <DownOutlined/>
                    </span>
                </Dropdown>
            </div>
            <Modal
                wrapClassName={classes.modalWrap}
                footer={null}
                title={title}
                centered
                visible={modal}
                onCancel={() => onCancel()}
            >
                <div className={classes.modalSubTitleWrap}>
                    <span className={classes.modalSubTitle}>{device && (device?.title || "Device")}</span>
                </div>
                <div className={classes.modalDescriptionWrap}>
                    {
                        isWarningLoading
                            ?
                            <Spin/>
                            :
                            <span className={classes.modalDescription}>
                        {
                            warnings?.length > 0
                                ?
                                <div>
                                    <div style={{marginBottom: "10px"}}>
                                        <span>There are {warnings?.length} warnings for this device:</span>
                                    </div>
                                    {warningsList}
                                </div>
                                :
                                "There are no warnings for this device"
                        }
                    </span>
                    }

                </div>
                <div className={classes.modalButton}>
                    <CustomButton width="70px"
                                  height="40px"
                                  padding="0"
                                  htmlType="button"
                                  disabled={isWarningLoading}
                                  onClick={onAddWarning}
                    >
                        {warnings?.length > 0 ? "MODIFY" : "ADD"}
                    </CustomButton>
                </div>
            </Modal>
        </React.Fragment>
    );
};

export default SettingPopup;
